import { map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Workflow } from '../common/review-types';
import { PreviewService } from './preview.service';
import { EmailPreviewSettings, ReviewGenSettingsService } from '../core/review-gen-settings.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AppConfigService } from '../core/app-config.service';
import { Template, TemplateListingService } from '../core/template-listing.service';

@Component({
  selector: 'app-preview-send-email',
  templateUrl: './preview-send-email.component.html',
  styleUrls: ['./preview-send-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PreviewSendEmailComponent implements OnInit {
  @Input() emailType: Workflow;
  @ViewChild('frame')
  public iframe: ElementRef;

  public htmlPreview$: BehaviorSubject<string> = new BehaviorSubject<string>(``);
  isPreviewLoading = true;
  previewHtml = '';
  public previewTemplates$: Observable<Template[]>;
  public selectTemplate: UntypedFormGroup;
  shouldShowDropdown$: Observable<boolean>;

  @Output() selectedTemplate: EventEmitter<string> = new EventEmitter<string>();

  constructor(
    private readonly previewEmailService: PreviewService,
    private readonly reviewGenSettingsService: ReviewGenSettingsService,
    private templateListService: TemplateListingService,
    private fb: UntypedFormBuilder,
    private appConfigService: AppConfigService,
    private reviewGenService: ReviewGenSettingsService,
  ) {}

  ngOnInit(): void {
    this.selectTemplate = this.fb.group({
      select: null,
    });

    this.shouldShowDropdown$ = this.appConfigService.appConfig.pipe(
      map((config) => {
        return config.isPaidVersion && this.emailType !== Workflow.FollowUp;
      }),
    );

    this.shouldShowDropdown$
      .pipe(
        switchMap((shouldShowDropdown) => {
          if (shouldShowDropdown) {
            this.reviewGenService.getDefaultTemplate();
            this.reviewGenService.defaultTemplateId$.subscribe((templateId) => {
              this.selectTemplate.setValue({
                select: templateId,
              });
            });
            this.templateListService.listTemplates();
            this.previewTemplates$ = this.templateListService.templates$;

            return this.selectTemplate.valueChanges.pipe(
              tap((value) => {
                this.insertHtml('');
                this.isPreviewLoading = true;
                this.selectedTemplate.emit(value.select);
              }),
              switchMap((selectTemplateForm) => {
                return this.previewEmailService.getEmailPreview(selectTemplateForm.select);
              }),
            );
          } else {
            const previewSettings$: Observable<EmailPreviewSettings> =
              this.reviewGenSettingsService.getEmailPreviewSettings();

            return previewSettings$.pipe(
              tap((previewSettings) => {
                this.selectTemplate = this.fb.group({
                  select: previewSettings.reviewGenType,
                });
                this.selectedTemplate.emit(previewSettings.reviewGenType);
              }),
              switchMap((previewSettings) => {
                let emailToPreview = previewSettings.reviewGenType;
                if (this.emailType === Workflow.FollowUp) {
                  emailToPreview = Workflow.FollowUp;
                }
                return this.previewEmailService.getEmailPreview(emailToPreview);
              }),
            );
          }
        }),
      )
      .subscribe((html) => {
        this.previewHtml = html;
      });
  }

  insertHtml(html: string): void {
    this.htmlPreview$.next(html);
    this.isPreviewLoading = false;
  }
}
